import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from './client';
import { buildQS } from './notifications';

export interface WebhookEndpointDto {
  id: string;
  orgId: string;
  productId: string | null;
  url: string;
  createdAt: string;
}

export interface WebhookEndpointWithSecret extends WebhookEndpointDto {
  signingSecret: string;
}

export interface CreateWebhookEndpointInput {
  orgId: string;
  url: string;
  productId?: string;
}

const webhookEndpointsKey = (orgId: string) => ['webhook-endpoints', orgId] as const;

export function useWebhookEndpoints(orgId: string) {
  return useQuery({
    queryKey: webhookEndpointsKey(orgId),
    queryFn: () =>
      apiGet<WebhookEndpointDto[]>(`/api/notifications/webhook-endpoints${buildQS({ orgId })}`),
    enabled: !!orgId,
  });
}

export function useCreateWebhookEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWebhookEndpointInput) =>
      apiPost<WebhookEndpointWithSecret>('/api/notifications/webhook-endpoints', input),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: webhookEndpointsKey(vars.orgId) }),
  });
}

export function useDeleteWebhookEndpoint(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/notifications/webhook-endpoints/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: webhookEndpointsKey(orgId) }),
  });
}
